import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackLanguageChange } from '../utils/analytics';

/**
 * Detect Browser Language
 * 
 * Detects user's preferred language from browser settings.
 * Supports multiple language formats (e.g., 'es', 'es-MX', 'en-US')
 * 
 * @returns {string} 'en' or 'es'
 */
const detectBrowserLanguage = () => {
  // Get browser language (e.g., 'es-MX', 'en-US', 'es', 'en')
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  
  // Extract primary language code (e.g., 'es' from 'es-MX')
  const primaryLang = browserLang.toLowerCase().split('-')[0];
  
  // Return 'es' for Spanish variants, 'en' for everything else
  return primaryLang === 'es' ? 'es' : 'en';
};

/**
 * Language Context
 * 
 * Provides language state management for the entire application.
 * Supports English (en) and Spanish (es) with:
 * - Automatic browser language detection
 * - localStorage persistence
 * - Manual override capability
 * 
 * Priority order:
 * 1. User's manual selection (localStorage)
 * 2. Browser language detection (first visit)
 * 3. Default: English
 * 
 * @example
 * const { language, setLanguage, toggleLanguage, isEnglish, isSpanish } = useLanguage();
 */
const LanguageContext = createContext();

/**
 * useLanguage Hook
 * 
 * Access language context from any component.
 * Must be used within a LanguageProvider.
 * 
 * @returns {Object} Language context value
 * @throws {Error} If used outside LanguageProvider
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

/**
 * LanguageProvider Component
 * 
 * Wraps the application to provide language state.
 * Features:
 * - Automatic browser language detection on first visit
 * - localStorage persistence for user preferences
 * - Manual language selection always takes priority
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Priority 1: Check if user has already set a preference
    const savedLanguage = localStorage.getItem('language');
    
    if (savedLanguage) {
      // User has made a manual selection - respect it
      return savedLanguage;
    }
    
    // Priority 2: First visit - detect browser language
    const detectedLanguage = detectBrowserLanguage();
    
    // Save the detected language for future visits
    localStorage.setItem('language', detectedLanguage);
    
    console.log(`🌍 Language auto-detected: ${detectedLanguage} (from browser: ${navigator.language})`);
    
    return detectedLanguage;
  });

  useEffect(() => {
    // Save to localStorage whenever language changes (manual selection)
    localStorage.setItem('language', language);
    
    // Track language change in Google Analytics
    trackLanguageChange(language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    isEnglish: language === 'en',
    isSpanish: language === 'es'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};